import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, Plus, Minus, Package } from "lucide-react";
import { FLAVORS } from "../lib/content";
import { useCart } from "../lib/cart";

export const BundleModal = ({ open, onClose }) => {
  const [picks, setPicks] = useState({});
  const { add, setOpen: setCartOpen } = useCart();

  const total = Object.values(picks).reduce((s, n) => s + n, 0);

  const change = (id, delta) =>
    setPicks((prev) => {
      const next = { ...prev, [id]: Math.max(0, (prev[id] || 0) + delta) };
      if (delta > 0 && total >= 3) return prev;
      if (next[id] === 0) delete next[id];
      return next;
    });

  const addBundle = () => {
    Object.entries(picks).forEach(([id, qty]) => {
      const flavor = FLAVORS.find((f) => f.id === id);
      if (flavor && qty > 0) add(flavor, qty);
    });
    toast.success("3-pack added. $17.99 per bag pricing unlocked.");
    setPicks({});
    onClose();
    setCartOpen(true);
  };

  const close = () => { setPicks({}); onClose(); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-void/80 backdrop-blur-md"
          onClick={close}
          role="dialog"
          aria-modal="true"
          data-testid="bundle-modal"
        >
          <motion.div
            initial={{ scale: 0.92, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 20, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-ash border border-white/10 rounded-xl p-6 md:p-8 max-h-[88vh] overflow-y-auto"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ember flex items-center gap-2">
                  <Package size={13} /> Bundle Builder
                </p>
                <h3 className="font-display font-black uppercase tracking-tight text-cream text-2xl md:text-3xl mt-2">
                  Build your <span className="font-serif italic font-normal lowercase text-ember">3-pack</span>
                </h3>
                <p className="font-mono text-xs text-cream/70 mt-2">Mix any 3 bags and every bag drops to $17.99.</p>
              </div>
              <button onClick={close} data-testid="bundle-close-button" className="text-cream/75 hover:text-ember transition-colors shrink-0">
                <X size={22} />
              </button>
            </div>

            <div className="flex items-center gap-2 mt-6" data-testid="bundle-slots">
              {[0, 1, 2].map((i) => {
                const flat = Object.entries(picks).flatMap(([id, n]) => Array(n).fill(id));
                const f = FLAVORS.find((fl) => fl.id === flat[i]);
                return (
                  <div
                    key={i}
                    data-testid={`bundle-slot-${i}`}
                    className={`flex-1 h-11 rounded-lg border flex items-center justify-center font-mono text-[10px] uppercase tracking-wider px-2 truncate ${
                      f ? "border-ember/60 text-cream bg-void" : "border-dashed border-white/15 text-cream/30"
                    }`}
                    style={f ? { boxShadow: `inset 0 -3px 0 ${f.accent}` } : undefined}
                  >
                    {f ? f.name : `Bag ${i + 1}`}
                  </div>
                );
              })}
            </div>

            <div className="mt-6 space-y-2.5">
              {FLAVORS.map((f) => (
                <div key={f.id} data-testid={`bundle-flavor-${f.id}`} className="flex items-center gap-3 border border-white/10 rounded-lg px-4 py-3">
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: f.accent }} />
                  <span className="flex-1 font-display font-extrabold uppercase text-cream text-sm truncate">{f.name}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => change(f.id, -1)}
                      disabled={!picks[f.id]}
                      data-testid={`bundle-dec-${f.id}`}
                      className="w-7 h-7 rounded-full border border-white/15 text-cream/70 hover:border-ember hover:text-ember transition-colors flex items-center justify-center disabled:opacity-25"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="font-mono text-sm text-cream w-4 text-center">{picks[f.id] || 0}</span>
                    <button
                      onClick={() => change(f.id, 1)}
                      disabled={total >= 3}
                      data-testid={`bundle-inc-${f.id}`}
                      className="w-7 h-7 rounded-full border border-ember/40 text-ember hover:bg-ember hover:text-void transition-colors flex items-center justify-center disabled:opacity-25"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addBundle}
              disabled={total !== 3}
              data-testid="bundle-add-button"
              className="mt-6 w-full bg-ember text-void font-mono text-xs uppercase tracking-widest py-4 rounded-full font-medium hover:bg-emberDark transition-colors disabled:opacity-40"
            >
              {total === 3 ? "Add 3-pack to bag · $53.97" : `Pick ${3 - total} more ${3 - total === 1 ? "bag" : "bags"}`}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
